import _isEmpty from 'lodash/isEmpty';
import _uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import Alert from 'src/components/alert';
import ConvertedUnits from 'src/components/ConvertedUnits';
import Loader from 'src/components/loader';
import { MODULE_TRANSACTIONS_CONTAINER_TYPES, MODULE_TRANSACTIONS_FILTER_STATES } from 'src/constants/filters';
import { useBatch, useBatchActionsByUri } from 'src/hooks/services/useBatch';
import { useContainersByUris } from 'src/hooks/services/useContainer';
import { useAllTransactions, useMergedTransactions } from 'src/hooks/services/useLps';
import { api } from 'src/utils/api';
import { API_RESOURCES } from 'src/utils/constants';
import { convertToUserUnits, formatThreeDecimalsNumber } from 'src/utils/conversions';
import { getUuid } from 'src/utils/url';

import FilterBar from './filter-bar';
import TransactionsTableSection from './transactions-table-section';

const ModuleTransactionsPanel = ({ inputModuleParentTransactionUri, module, handleRefetchCyclone }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editResetKey, setEditResetKey] = useState(0);
  const [editedReconciledTransactions, setEditedReconciledTransactions] = useState({});
  const [filterState, setFilterState] = useState(MODULE_TRANSACTIONS_FILTER_STATES.ALL);
  const [containerTypeFilter, setContainerTypeFilter] = useState(MODULE_TRANSACTIONS_CONTAINER_TYPES.ALL);

  const { addToast } = useToasts();

  // Create filters based on UI state
  const additionalFilters = useMemo(() => {
    const filters = {};

    // Apply completion state filter
    if (filterState === MODULE_TRANSACTIONS_FILTER_STATES.PENDING) {
      filters.isComplete = false;
    } else if (filterState === MODULE_TRANSACTIONS_FILTER_STATES.RECONCILED) {
      filters.isComplete = true;
    }

    // Apply container type filter when not "All"
    if (containerTypeFilter !== MODULE_TRANSACTIONS_CONTAINER_TYPES.ALL) {
      filters.containerType = containerTypeFilter.toLowerCase();
    }

    return filters;
  }, [filterState, containerTypeFilter]);

  // Get filtered transactions
  const { data: filteredTransactions = [], isInitialLoading: isLoadingTransactions, error: transactionsError, refetch: refetchFilteredTransactions } = useMergedTransactions(
    inputModuleParentTransactionUri,
    additionalFilters
  );

  // Get all transactions (source of truth)
  const { data: allTransactions = [], refetch: refetchAllTransactions } = useAllTransactions(inputModuleParentTransactionUri);

  // Extract unique container URIs from transactions
  const containerUrisFromTransactions = useMemo(() => {
    if (!filteredTransactions.length) return [];
    return _uniq(filteredTransactions.map(transaction => transaction.container));
  }, [filteredTransactions]);

  // Fetch containers data
  const {
    data: containers = {},
    isInitialLoading: isLoadingContainers,
    refetch: refetchContainers,
    error: containersError,
  } = useContainersByUris(containerUrisFromTransactions);

  // Fetch batch data for units
  const {
    data: moduleBatch,
    isInitialLoading: isLoadingBatch,
    refetch: refetchBatch,
    error: batchError,
  } = useBatch(module.current_batch);

  const transactionsBatchActions = [...new Set(filteredTransactions.map(transaction =>
    transaction.material_batch_action))]
    .filter(Boolean);

  const {
    data: batchActions,
    isInitialLoading: isLoadingBatchActions,
    error: batchActionsError,
  } = useBatchActionsByUri(transactionsBatchActions);

  const batchActionsProps = {
    batchActions,
    isLoadingBatchActions,
    batchActionsError,
  }

  const initialAvailableMaterial = useMemo(() => {
    // Rely on the full "allTransactions" dataset for initial quantity
    return formatThreeDecimalsNumber(allTransactions?.[0]?.initial_quantity_available) || 0;
  }, [allTransactions]);

  // Separate transactions by completion state
  const { pendingTransactions, reconciledTransactions } = useMemo(() => {
    if (_isEmpty(filteredTransactions)) {
      return { pendingTransactions: [], reconciledTransactions: [] };
    }

    if (filterState === MODULE_TRANSACTIONS_FILTER_STATES.PENDING) {
      return {
        pendingTransactions: filteredTransactions,
        reconciledTransactions: [],
      };
    }

    if (filterState === MODULE_TRANSACTIONS_FILTER_STATES.RECONCILED) {
      return {
        pendingTransactions: [],
        reconciledTransactions: filteredTransactions,
      };
    }

    // For ALL state, divide the transactions by completion state
    return {
      pendingTransactions: filteredTransactions.filter(t => !t.is_complete),
      reconciledTransactions: filteredTransactions.filter(t => t.is_complete),
    };
  }, [filteredTransactions, filterState]);

  // Calculate sum of reconciled transactions (from All Transactions)
  const sumOfReconciledTransactions = useMemo(() => {
    // Always use the full dataset to get the total reconciled quantity
    return allTransactions
      .filter(transaction => transaction.is_complete)
      .reduce((sum, transaction) => {
        const editedValue = editedReconciledTransactions[transaction.uri];
        const quantity = editedValue != null ? parseFloat(editedValue) : transaction.quantity || 0;
        return sum + quantity;
      }, 0);
  }, [allTransactions, editedReconciledTransactions]);

  // Calculate pending amount to be claimed
  const pendingAmountToBeClaimed = useMemo(() => {
    const initialAmount = initialAvailableMaterial - sumOfReconciledTransactions;
    return formatThreeDecimalsNumber(initialAmount);
  }, [initialAvailableMaterial, sumOfReconciledTransactions]);

  const onToggleEdit = () => {
    setIsEditing((prev) => !prev);
    setEditResetKey(prev => prev + 1); // Trigger input reset
    setEditedReconciledTransactions({});
  };

  const handleRetry = () => {
    // refetch required data:
    // - transactions
    // - containers
    // - batch

    setFilterState(MODULE_TRANSACTIONS_FILTER_STATES.ALL);
    setContainerTypeFilter(MODULE_TRANSACTIONS_CONTAINER_TYPES.ALL);

    refetchAllTransactions();
    refetchContainers();
    refetchBatch();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Send each PUT sequentially
      for (const [uri, newQuantity] of Object.entries(editedReconciledTransactions)) {
        const original = allTransactions.find(t => t.uri === uri);
        if (!original) continue;

        const payload = {
          quantity: parseFloat(newQuantity),
          // eslint-disable-next-line camelcase
          is_complete: true,
        };

        await api.put(`${API_RESOURCES.MATERIAL_BATCH_ACTION_TRANSACTION}/${getUuid(uri)}/`, { json: payload });
      }

      // Wait for all PUTs to finish
      await Promise.all([
        refetchAllTransactions(),
        refetchFilteredTransactions(),
        handleRefetchCyclone(),
      ]);

      addToast('The reconciled amount has been successfully updated.', { appearance: 'success' });
    } catch (error) {
      console.error('Error updating reconciled transactions:', error);
      addToast('Failed to update reconciled transactions.', { appearance: 'error' });
    } finally {
      setEditedReconciledTransactions({});
      onToggleEdit();
      setIsSubmitting(false);
    }
  };

  const {
    quantity: convertedInitialAvailableAmount,
  } = convertToUserUnits(initialAvailableMaterial - sumOfReconciledTransactions, moduleBatch?.units);

  // Determine loading state
  const isLoading = isLoadingTransactions || isLoadingContainers || isLoadingBatch;
  const hasError = transactionsError || containersError || batchError;

  const isEditable = allTransactions.some(transaction => transaction.is_editable === true);

  if (hasError) {
    return (
      <div className="alert alert-danger">
        <p>Error loading transaction data</p>
        <button type="button" className="btn btn-outline-danger" onClick={handleRetry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {isLoading ? (
        <Loader isMedium showText={false} />
      ) : (
        <div>
          <p className="mb-3">
            <strong>Initial Available Material:</strong>{' '}
            <ConvertedUnits
              displayZero
              quantity={initialAvailableMaterial}
              units={moduleBatch?.units}
            />
          </p>

          <FilterBar
            filterState={filterState}
            setFilterState={setFilterState}
            containerTypeFilter={containerTypeFilter}
            setContainerTypeFilter={setContainerTypeFilter}
          />

          {(filterState === MODULE_TRANSACTIONS_FILTER_STATES.ALL
            || filterState === MODULE_TRANSACTIONS_FILTER_STATES.PENDING) && (
            <TransactionsTableSection
              title="Pending"
              summary={pendingAmountToBeClaimed}
              units={moduleBatch?.units}
              transactions={pendingTransactions}
              containers={containers}
              batchActionsProps={batchActionsProps}
            />
          )}

          {(filterState === MODULE_TRANSACTIONS_FILTER_STATES.ALL
            || filterState === MODULE_TRANSACTIONS_FILTER_STATES.RECONCILED) && (
            <>
              <TransactionsTableSection
                title="Reconciled"
                summary={sumOfReconciledTransactions}
                units={moduleBatch?.units}
                transactions={reconciledTransactions}
                containers={containers}
                batchActionsProps={batchActionsProps}
                isEditable={isEditable && reconciledTransactions.length > 0}
                isEditing={isEditing}
                editResetKey={editResetKey}
                amountLeftToReclaim={convertedInitialAvailableAmount}
                updateReconciledTransactions={setEditedReconciledTransactions}
                onToggleEdit={onToggleEdit}
              />
              {pendingAmountToBeClaimed < 0 && (
                <Alert variant="warning" className="text-center" name="reconciledAmountWarning">
                  You&apos;ve entered more than the available material to be claimed. Please reduce the amount so your changes can be saved.
                </Alert>
              )}
              {isEditable && isEditing && reconciledTransactions.length > 0 && (
                <button
                  type='button'
                  className='btn btn-primary action-btn btn-block mt20'
                  disabled={_isEmpty(editedReconciledTransactions) || pendingAmountToBeClaimed < 0}
                  onClick={handleSubmit}
                >
                  {!isSubmitting ? 'Save' : <Loader isSmall className="transaction-loader" showText={false} />}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

ModuleTransactionsPanel.propTypes = {
  inputModuleParentTransactionUri: PropTypes.string.isRequired,
  module: PropTypes.shape({
    // eslint-disable-next-line camelcase
    current_batch: PropTypes.string,
  }).isRequired,
  handleRefetchCyclone: PropTypes.func.isRequired,
};

export default ModuleTransactionsPanel;
